import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/Spinner';
import { VideoTable } from '../../components/videos/VideoTable';
import { TuningPanel } from '../../components/videos/TuningPanel';
import {
  useVideosQuery,
  useVideoTuningQuery,
  useVideoSettingsQuery,
  useUpdateVideoSettingsMutation,
  useTranscribeBackfillMutation,
  useTranscribeVideoMutation,
  useTranslateBackfillMutation,
  useCaptionCoverageQuery,
  videoKeys,
} from '../../hooks/useVideos';
import { useQueryClient } from '@tanstack/react-query';

export default function VideosPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: listData, isLoading: listLoading, error: listError } = useVideosQuery();
  const { data: tuning } = useVideoTuningQuery();
  const { data: settings } = useVideoSettingsQuery();
  const { data: coverage } = useCaptionCoverageQuery();

  const backfill = useTranscribeBackfillMutation();
  const translateBackfill = useTranslateBackfillMutation();
  const transcribe = useTranscribeVideoMutation();
  const updateSettings = useUpdateVideoSettingsMutation();

  const handleBackfill = async () => {
    await backfill.mutateAsync();
    queryClient.invalidateQueries({ queryKey: videoKeys.list() });
  };

  const handleTranslateBackfill = async () => {
    await translateBackfill.mutateAsync();
    queryClient.invalidateQueries({ queryKey: videoKeys.list() });
  };

  const handleTranscribe = async (stem: string) => {
    await transcribe.mutateAsync({ stem });
    queryClient.invalidateQueries({ queryKey: videoKeys.list() });
  };

  const handleToggleSuggestions = (enabled: boolean) => {
    updateSettings.mutate({ suggestions_enabled: enabled });
  };

  const handleMinSamplesChange = (value: number) => {
    updateSettings.mutate({ min_labeled_samples: value });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Video Library 🎬</h1>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleBackfill}
            disabled={backfill.isPending}
            className="text-sm px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfill.isPending ? 'Enqueueing…' : 'Backfill missing transcripts'}
          </button>
          <button
            onClick={handleTranslateBackfill}
            disabled={translateBackfill.isPending}
            className="text-sm px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {translateBackfill.isPending ? 'Enqueueing…' : 'Backfill translations'}
          </button>
        </div>
      </div>

      {coverage && (
        <p className="text-sm text-gray-600">
          Captions: {coverage.with_caption} / {coverage.total}
          {coverage.missing.length > 0 && (
            <span className="text-amber-600"> · {coverage.missing.length} missing</span>
          )}
        </p>
      )}

      {listError && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {listError.message.includes('503')
            ? 'Video library is temporarily unavailable (mount offline).'
            : `Failed to load videos: ${listError.message}`}
        </div>
      )}

      {tuning && settings && (
        <TuningPanel
          tuning={tuning}
          onToggleSuggestions={handleToggleSuggestions}
          onMinSamplesChange={handleMinSamplesChange}
        />
      )}

      {listLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size={40} />
        </div>
      ) : listData?.videos ? (
        <VideoTable
          videos={listData.videos}
          onRowClick={(stem) => navigate(`/videos/${stem}`)}
          onTranscribe={handleTranscribe}
        />
      ) : null}
    </div>
  );
}
